import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, Menu } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { IMAGES, LINKS, MOBILE_EXTRA_LINKS, NAV_LINKS } from "@/data/content";

const NavAnchor = ({ link, className, onClick, children }) => (
  <Link
    data-testid={`nav-link-${link.id}`}
    to={link.href}
    onClick={onClick}
    className={className}
    {...(link.external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
  >
    {children || link.label}
  </Link>
);

const useScrolled = () => {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 16);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return scrolled;
};

const ServicesDropdown = ({ link }) => (
  <div className="group relative" data-testid="nav-services-menu">
    <NavAnchor link={link} className="link-underline inline-flex items-center gap-1 text-sm font-medium text-ink/80 transition-colors hover:text-ink">
      {link.label} <ChevronDown className="h-3.5 w-3.5 transition-transform group-hover:rotate-180" />
    </NavAnchor>
    <div className="invisible absolute left-1/2 top-full z-50 -translate-x-1/2 pt-4 opacity-0 transition-[opacity,visibility] duration-200 group-hover:visible group-hover:opacity-100 group-focus-within:visible group-focus-within:opacity-100">
      <div className="flex min-w-[13rem] flex-col border border-navy/10 bg-white py-2">
        {link.children.map((c) => (
          <Link
            key={c.id}
            to={c.href}
            data-testid={`nav-link-${c.id}`}
            className="px-5 py-2.5 text-sm font-medium text-ink/80 hover:bg-snow hover:text-navy"
            {...(c.external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
          >
            {c.label}
          </Link>
        ))}
      </div>
    </div>
  </div>
);

const DesktopNav = () => (
  <nav className="hidden items-center gap-9 lg:flex">
    {NAV_LINKS.map((l) =>
      l.children ? (
        <ServicesDropdown key={l.id} link={l} />
      ) : (
        <NavAnchor key={l.id} link={l} className="link-underline text-sm font-medium text-ink/80 transition-colors hover:text-ink" />
      )
    )}
    <Button asChild data-testid="nav-cta-calendly" className="btn-primary">
      <a href={LINKS.calendly} target="_blank" rel="noopener noreferrer">Work with PKR</a>
    </Button>
  </nav>
);

const MOBILE_LINKS = [
  ...NAV_LINKS.filter((l) => l.id !== "careers"),
  ...MOBILE_EXTRA_LINKS,
  ...NAV_LINKS.filter((l) => l.id === "careers"),
];

const MobileNav = () => {
  const [open, setOpen] = useState(false);
  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <button data-testid="nav-mobile-menu-button" aria-label="Open menu" className="p-2 text-ink lg:hidden hover:bg-navy/10">
          <Menu className="h-6 w-6" />
        </button>
      </SheetTrigger>
      <SheetContent side="right" className="bg-white w-[85vw] sm:w-80" data-testid="nav-mobile-menu">
        <SheetTitle className="sr-only">Menu</SheetTitle>
        <div className="mt-10 flex flex-col gap-6">
          {MOBILE_LINKS.map((l) => (
            <div key={l.id} className="flex flex-col gap-3">
              <NavAnchor link={l} onClick={() => setOpen(false)} className="font-heading text-2xl font-semibold text-ink" />
              {l.children?.map((c) => (
                <NavAnchor key={c.id} link={c} onClick={() => setOpen(false)} className="pl-4 font-heading text-lg font-medium text-ink/75" />
              ))}
            </div>
          ))}
          <Button asChild data-testid="nav-mobile-cta-calendly" className="btn-primary mt-4 h-12 text-base">
            <a href={LINKS.calendly} target="_blank" rel="noopener noreferrer">Work with PKR</a>
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export const Navbar = () => {
  const scrolled = useScrolled();
  return (
    <header
      data-testid="site-navbar"
      className={`fixed inset-x-0 top-0 z-50 bg-white border-b transition-[border-color] duration-300 ${scrolled ? "border-navy/10" : "border-transparent"}`}
    >
      <div className="mx-auto flex h-20 items-center justify-between px-5 sm:px-8">
        <Link to="/" data-testid="nav-logo-link" className="flex items-center gap-3">
          <img src={IMAGES.logo} alt="PKR Recruiting by Kelly Robinson" className="h-11 w-auto" />
        </Link>
        <DesktopNav />
        <MobileNav />
      </div>
    </header>
  );
};
